import { useEffect, useState } from "react";
import {
  DEFAULT_VERSION_INFO,
  MaxTimeModifiers,
  BreakdownTableDepths,
  TaskTypes,
  safeRoundHours,
  zip,
  BreakdownColumns,
} from "@repo/utils";
import {
  DEFAULT_DESCRIPTION_COLUMN,
  DEFAULT_DESCRIPTION_COLUMN_IDENTIFIER,
  DEFAULT_BREAKDOWN_COLUMNS,
  blankStructureGenerator,
  structureCreator,
  modifyNewValuesWithCoefficients,
  generateEntryId,
  generateSectionId,
  generateTaskDescriptionId,
} from "@repo/structure";
import { useXlsImporterSettings, TemplatePreview } from "@tools";
import { CircularProgress, Grid } from "@material-ui/core";
import useStyle from "./Steps.style";
import { escapeHtml } from "@utils";

export const Preview = () => {
  const classes = useStyle();
  const settings = useXlsImporterSettings();

  const [ isLoading, setLoading ] = useState(true);

  const {
    useFastMode,
    fastModeMapping,
    usedWorkTypes,
    importedStructure,
    currentBreakdownData,
    importModules,
    useMinMax,
    applyReverseBuffers,
    importTimeModifier,
    descColumn,
    additionalTextColumns,
    defaultStructure,
  } = settings;

  useEffect(() => {
    (async () => {
      setLoading(true);
      const tableData = currentBreakdownData.sheet.data;

      let levels = [], level = -1;

      if(useFastMode) {
        fastModeMapping.forEach((column, i) => {
          if ( typeof column === "number" ) {
            level++;
            levels[ level ] = currentBreakdownData.sheet.data
              .map(( row, i ) => ({
                name: row[ column ],
                x: column,
                y: i,
              }))
              .filter((row => typeof row.name === "string" && row.name.length > 0))
          } else if(i === BreakdownTableDepths.SECTION) {
            level++;
            levels[level] = [{ name: "Section 1", x: -1, y: -1 }];
          }
        })
      } else {
        [
          importModules.filter(({ type }) => type === "section"),
          importModules.filter(({ type }) => type === "module"),
          importModules.filter(({ type }) => type === "feature"),
          importModules.filter(({ type }) => type === "task"),
        ].forEach((cells, i) => {
          if(cells.length > 0) {
            level++;
            levels[level] = cells.sort((a, b) => a.y - b.y);
          } else if(i === BreakdownTableDepths.SECTION) {
            level++;
            levels[level] = [{ name: "Section 1", x: -1, y: -1 }];
          }
        })
      }

      // if (!levels[BreakdownTableDepths.SECTION].length)
      //   levels[BreakdownTableDepths.SECTION] = { name: "Section 1", x: -1, y: -1 };
      let structure;

      if(defaultStructure) {
        const copiedStructure = defaultStructure.serializeCore()
        copiedStructure.workTypes = [];
        copiedStructure.settings.timeModifier = importTimeModifier;
        copiedStructure.settings.useMinMax = useMinMax;
        copiedStructure.settings.viewLevel = BreakdownTableDepths.TASK;
        copiedStructure.settings.valueLevel = BreakdownTableDepths.TASK;
        delete copiedStructure.settings.roundPrice;
        structure = await structureCreator({
          zippedTemplateStructure: zip(JSON.stringify(copiedStructure)),
          workTypes: usedWorkTypes,
          // createNewTags: useNewWorkTypes
        });
        // delete importedStructureSettings.roundHours;
        // delete importedStructureSettings.roundType;
      } else {
        structure = blankStructureGenerator({
          workTypes: usedWorkTypes,
          // defaultCurrency: this.defaultCurrency,
          // defaultCompanyCurrency: this.defaultCurrency,
          // defaultTimeEquivalents: defaultTimeEquiv,
          defaultTimeModifier: importTimeModifier,
          defaultMinMax: useMinMax,
          // noPrices: !librarySettings?.showPrices,
          noSections: true,
        });
      }

      const importedWorkTypes = usedWorkTypes.map((workType, index) => ({
        ...workType,
        structureId: structure.workTypes[index]?.id,
      })).filter(({ structureId }) => structureId);

      structure.workTypes.forEach((workType) => {
        if (typeof workType.systemId !== "number")
          delete workType.systemId;
      });

      structure.sections.forEach((section) => {
        section.workTypes = importedWorkTypes.map(({ structureId, name }) => ({ id: structureId, name }));
      });

      const modifier = (structure.settings.timeModifier || structure.settings.dayModifier) * MaxTimeModifiers.HOUR;

      const getWorkTypeValues = (y) => {
        if(typeof y !== "number" || y < 0 || !tableData[y]) return;
        return importedWorkTypes.reduce((val, wT) => {
          let values = {};
          let valueMin = ( parseFloat(tableData[y][wT.column]) * modifier ) || 0;
          let valueMax = ( parseFloat(tableData[y][wT.columnMax]) * modifier ) || 0;

          if(applyReverseBuffers) {
            const percent = structure.settings.buffers?.reduce((p, ov) => {
              if(ov.used && ov.workTypes.includes(wT.structureId))
                return p + ov.percent;
              return p;
            }, 0);

            if(percent) {
              if(valueMin)
                valueMin = safeRoundHours((valueMin / (1 + percent/100)), modifier);
              if(valueMax)
                valueMax = safeRoundHours(valueMax / (1 + percent/100), modifier);
            }
          }

          if(useMinMax) {
            if(valueMin)
              values.min = valueMin;
            if(valueMax)
              values.max = valueMax;
          } else
            values.avg = valueMin;

          values = modifyNewValuesWithCoefficients(
            values,
            Object.keys(values),
            values,
            modifier,
            undefined,
            true,
          )

          return { ...val, [wT.structureId]: values };
        }, {});
      };

      const descriptionColumns = [
        {
          id: DEFAULT_DESCRIPTION_COLUMN_IDENTIFIER,
          column: descColumn,
        },
        ...additionalTextColumns.map((textColumn, index) => ({
          id: generateTaskDescriptionId(index + 1),
          column: textColumn.column,
        })),
      ];

      const descriptionBreakdownColumns = additionalTextColumns.map((textColumn, index) => ({
        ...DEFAULT_DESCRIPTION_COLUMN,
        name: BreakdownColumns.DESCRIPTION,
        visible: true,
        descriptionId: generateTaskDescriptionId(index + 1),
        displayName: textColumn.name || `Text column ${index + 2}`,
      }));

      const descriptionColumnIndex = DEFAULT_BREAKDOWN_COLUMNS.findIndex(
        column => column.descriptionId === DEFAULT_DESCRIPTION_COLUMN_IDENTIFIER
      );
      const usedBreakdownColumns = [
        ...DEFAULT_BREAKDOWN_COLUMNS.slice(0, descriptionColumnIndex + 1),
        ...descriptionBreakdownColumns,
        ...DEFAULT_BREAKDOWN_COLUMNS.slice(descriptionColumnIndex + 1),
      ];

      const getDescription = (y, children) => {
        if(children?.find(child => child.y === y))
          return;

        return descriptionColumns.reduce((descriptions, { id, column }) => ({
          ...descriptions,
          [id]: {
            content: typeof y === "number" && y >= 0 && tableData[ y ]
              ? tableData[ y ][ column ] || ""
              : ""
          },
        }), {});
      }

      let index = 0;

      structure.sections = levels[BreakdownTableDepths.SECTION].map((section, i, l) => {
        const nextSectionRow = l[i + 1]?.y || Infinity;
        const submodules = levels[BreakdownTableDepths.MODULE]
          ?.filter(({ y }) => y >= section.y && y < nextSectionRow) || [];

        return {
          id: generateSectionId(++index),
          lvl: BreakdownTableDepths.SECTION,
          name: escapeHtml(section.name).trim(),
          type: TaskTypes.WORKLOAD,
          descriptions: getDescription(section.y, submodules),
          isOpened: !!submodules.length,
          workTypes: importedWorkTypes.map(({ structureId: id, name }) => ({ id, name })),
          usedBreakdownColumns,
          values:
            level === BreakdownTableDepths.SECTION || !submodules.length
              ? getWorkTypeValues(section.y)
              : {},
          children: submodules
            .map((module, i, l) => {
              const nextModuleRow = l[i + 1]?.y || Infinity;
              const submodules = levels[BreakdownTableDepths.FEATURE]
                ?.filter(({ y }) => y >= module.y && y < nextModuleRow && y < nextSectionRow) || [];

              return {
                id: generateEntryId(++index),
                lvl: BreakdownTableDepths.MODULE,
                name: escapeHtml(module.name).trim(),
                type: TaskTypes.WORKLOAD,
                descriptions: getDescription(module.y, submodules),
                isOpened: !!submodules.length,
                values:
                  level === BreakdownTableDepths.MODULE || !submodules.length
                    ? getWorkTypeValues(module.y)
                    : {},
                children: submodules.map((feature, i, l) => {
                  const nextFeatureRow = l[i + 1]?.y || Infinity;
                  const submodules = levels[BreakdownTableDepths.TASK]
                    ?.filter(({ y }) => y >= feature.y && y < nextFeatureRow && y < nextModuleRow && y < nextSectionRow) || [];

                  return {
                    id: generateEntryId(++index),
                    lvl: BreakdownTableDepths.FEATURE,
                    name: escapeHtml(feature.name).trim(),
                    type: TaskTypes.WORKLOAD,
                    descriptions: getDescription(feature.y, submodules),
                    isOpened: !!submodules.length,
                    values:
                      level === BreakdownTableDepths.FEATURE || !submodules.length
                        ? getWorkTypeValues(feature.y)
                        : {},
                    children: submodules.map((task) => ({
                      id: generateEntryId(++index),
                      lvl: BreakdownTableDepths.TASK,
                      name: escapeHtml(task.name).trim(),
                      type: TaskTypes.WORKLOAD,
                      descriptions: getDescription(task.y),
                      values: getWorkTypeValues(task.y),
                    })),
                  };
                }),
              };
            }),
        };
      });

      if(structure.settings.buffers)
        structure.settings.buffers = structure.settings.buffers?.map((ov) => ({
          ...ov,
          used: false,
        }))

      settings.handleSetImportedStructure(
        zip(JSON.stringify(structure))
      );

      setLoading(false);
    })();
  }, []);

  return (
    <Grid item container className={classes.previewRoot}>
      {
        isLoading &&
        <CircularProgress color="primary" size={48} className={classes.previewSpinner} />
      }
      {
        !isLoading &&
        <TemplatePreview
          // useNewWorkTypes
          versionStructure={importedStructure}
          workTypes={usedWorkTypes}
          versionData={[DEFAULT_VERSION_INFO]}
        />
      }
    </Grid>
  );
};
